import {createContext, use, useCallback, useMemo, useRef, useState} from 'react'

export interface ChatStreamMessage {
  role: 'user' | 'assistant'
  content: string
  id?: string
  threadID?: string
}

export type StreamingState = 'requested' | 'receiving' | 'closed'

interface ChatStreamContextValue {
  setPendingChat: (chat: string) => void
  clearPendingChat: () => void
  setStreamingChatResponse: (sessionId: string, response: string | null) => void
  setStreamingState: (state: StreamingState) => void
  streamingState: StreamingState
  getChatMessageHistory: (sessionId: string) => ChatStreamMessage[]
  pendingChatMessage: ChatStreamMessage | null
  latestChatSessionId: string | null
}

export const ChatStreamContext = createContext<ChatStreamContextValue | undefined>(undefined)

interface ChatStreamProviderProps {
  children: React.ReactNode
}

// ChatStreamProvider is the central play in holding onto streaming chat messages as they
// come in, with enough info around them to be able to merge them into the list of logs
// that have been loaded from the server.
//
// Pending messages are held separately from other chat streaming state because a pending
// message may start looking like it's in one session, but then because of intent
// detection shift to a new session (i.e. your chat message triggers a new agent).
// These are a little more flickery than ideal on those transitions, but the overall
// approach here at least keeps the main chat content stable once it's displayed.
export function ChatStreamProvider({children}: ChatStreamProviderProps) {
  const [streamingState, setStreamingState] = useState<StreamingState>('closed')
  const [chatHistoryBySession, setChatHistoryBySession] = useState<Record<string, ChatStreamMessage[]>>({})
  const [pendingChatMessage, setPendingChatMessage] = useState<ChatStreamMessage | null>(null)
  const pendingChatMessageRef = useRef<ChatStreamMessage | null>(null)
  const [latestChatSessionId, setLatestChatSessionId] = useState<string | null>(null)

  // Store the user message as pending state
  const setPendingChat = useCallback((chat: string) => {
    if (chat) {
      const msg: ChatStreamMessage = {role: 'user', content: chat, id: '', threadID: ''}
      pendingChatMessageRef.current = msg
      setPendingChatMessage(msg)
    }
  }, [])

  const clearPendingChat = useCallback(() => {
    pendingChatMessageRef.current = null
    setPendingChatMessage(null)
  }, [])

  // When the first streaming chunk arrives, flush the pending chat into
  // the committed history before appending the assistant message.
  const setStreamingChatResponse = useCallback((sessionId: string, response: string | null) => {
    if (response) {
      setStreamingState('receiving')
      if (sessionId) {
        setLatestChatSessionId(sessionId)
      }
      const pending = pendingChatMessageRef.current
      pendingChatMessageRef.current = null

      setChatHistoryBySession(prev => {
        const sessionHistory = prev[sessionId] ?? []

        // If there's a pending user message this is a new exchange — flush it
        // and start a new assistant entry rather than updating the previous one.
        if (pending) {
          return {
            ...prev,
            [sessionId]: [
              ...sessionHistory,
              pending,
              {role: 'assistant' as const, content: response, id: '', threadID: ''},
            ],
          }
        }

        const last = sessionHistory[sessionHistory.length - 1]
        if (last && last.role === 'assistant') {
          return {
            ...prev,
            [sessionId]: [
              ...sessionHistory.slice(0, -1),
              {role: 'assistant' as const, content: response, id: '', threadID: ''},
            ],
          }
        }

        return {
          ...prev,
          [sessionId]: [...sessionHistory, {role: 'assistant' as const, content: response, id: '', threadID: ''}],
        }
      })
      setPendingChatMessage(null)
    }
  }, [])

  const getChatMessageHistory = useCallback(
    (sessionId: string) => {
      const history = chatHistoryBySession[sessionId] ?? []
      if (pendingChatMessage && sessionId === latestChatSessionId) {
        return [...history, pendingChatMessage]
      }
      return history
    },
    [chatHistoryBySession, pendingChatMessage, latestChatSessionId],
  )

  const value = useMemo(
    () => ({
      setPendingChat,
      clearPendingChat,
      setStreamingChatResponse,
      setStreamingState,
      streamingState,
      getChatMessageHistory,
      pendingChatMessage,
      latestChatSessionId,
    }),
    [
      setPendingChat,
      clearPendingChat,
      setStreamingChatResponse,
      streamingState,
      getChatMessageHistory,
      pendingChatMessage,
      latestChatSessionId,
    ],
  )

  return <ChatStreamContext value={value}>{children}</ChatStreamContext>
}

export function useChatStreamContext(): ChatStreamContextValue {
  const context = use(ChatStreamContext)
  if (!context) {
    throw new Error('useChatStreamContext must be used within a ChatStreamProvider')
  }
  return context
}
